import React, { lazy, Component } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Clock, Navigation, Calendar, MessageCircle } from 'lucide-react';
interface BranchProps {
  name: string;
  address: string;
  mapsQuery: string;
  bookingUrl: string | null;
  bookingLabel: string;
  bookingNote?: string;
  bookingType?: 'setmore' | 'whatsapp';
  delay: number;
}
function BranchCard({
  name,
  address,
  mapsQuery,
  bookingUrl,
  bookingLabel,
  bookingNote,
  bookingType = 'setmore',
  delay
}: BranchProps) {
  const mapsEmbedUrl = `https://www.google.com/maps?q=${encodeURIComponent(mapsQuery)}&output=embed`;
  const mapsLinkUrl = `https://www.google.com/maps?q=${encodeURIComponent(mapsQuery)}`;
  return <motion.div initial={{
    opacity: 0,
    y: 25
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.6,
    delay
  }} className="bg-dark-card border border-gold/10 rounded-2xl overflow-hidden shadow-xl flex flex-col" data-id="element-204">
      {/* Map embed */}
      <div className="w-full h-48 md:h-56" data-id="element-205">
        <iframe src={mapsEmbedUrl} width="100%" height="100%" style={{
        border: 0,
        filter: 'invert(90%) hue-rotate(180deg) contrast(100%)'
      }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title={`Mapa ${name}`} className="w-full h-full" data-id="element-206" />
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-1" data-id="element-207">
        {/* Branch name */}
        <h3 className="text-xl font-bold text-white mb-3" data-id="element-208">{name}</h3>

        {/* Address */}
        <div className="flex items-start gap-2.5 mb-4" data-id="element-209">
          <MapPin size={16} className="text-gold flex-shrink-0 mt-0.5" data-id="element-210" />
          <p className="text-gray-300 text-sm" data-id="element-211">{address}</p>
        </div>

        {/* Hours */}
        <div className="flex items-start gap-2.5 mb-6" data-id="element-212">
          <Clock size={16} className="text-gold flex-shrink-0 mt-0.5" data-id="element-213" />
          <div className="text-sm text-gray-400 space-y-0.5" data-id="element-214">
            <p data-id="element-215">Lunes a Sábado: 10:00 – 20:00</p>
            <p data-id="element-216">Domingo: 09:00 – 14:00</p>
          </div>
        </div>

        {/* Spacer to push buttons to bottom */}
        <div className="flex-1" data-id="element-217" />

        {/* CTAs */}
        <div className="flex flex-col gap-3" data-id="element-218">
          {bookingUrl ? bookingType === 'whatsapp' ? <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BD5A] text-white font-bold py-3 px-5 rounded-xl transition-all duration-300 text-sm shadow-[0_0_20px_rgba(37,211,102,0.2)]" data-id="element-219">
                <MessageCircle size={16} data-id="element-220" />
                <span data-id="element-221">{bookingLabel}</span>
              </a> : <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-dark font-bold py-3 px-5 rounded-xl transition-all duration-300 text-sm" data-id="element-222">
                <Calendar size={16} data-id="element-223" />
                <span data-id="element-224">{bookingLabel}</span>
              </a> : <div data-id="element-225">
              <button disabled className="w-full flex items-center justify-center gap-2 bg-gold/40 text-dark/60 font-bold py-3 px-5 rounded-xl text-sm cursor-not-allowed" data-id="element-226">
                <Calendar size={16} data-id="element-227" />
                <span data-id="element-228">{bookingLabel}</span>
              </button>
              {bookingNote && <p className="text-gold/60 text-xs text-center mt-2 italic" data-id="element-229">
                  {bookingNote}
                </p>}
            </div>}
          <a href={mapsLinkUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 border border-gold/30 text-gold hover:bg-gold/10 font-semibold py-3 px-5 rounded-xl transition-all duration-300 text-sm" data-id="element-230">
            <Navigation size={16} data-id="element-231" />
            <span data-id="element-232">Cómo llegar</span>
          </a>
        </div>
      </div>
    </motion.div>;
}
export function LocationSection() {
  return <section id="sucursales" className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-233">
      {/* Header */}
      <div className="text-center mb-14" data-id="element-234">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-3xl md:text-4xl font-bold mb-4" data-id="element-235">
          Sucursales
        </motion.h2>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="text-gray-400 max-w-xl mx-auto" data-id="element-236">
          Elegí la sucursal que te quede mejor y coordiná tu turno.
        </motion.p>
      </div>

      {/* 2-column grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" data-id="element-237">
        <BranchCard name="Sucursal Principal" address="Avenida Artigas 530, Las Piedras, Canelones" mapsQuery="Avenida Artigas 530, Las Piedras, Canelones, Uruguay" bookingUrl="https://lucianobarbershop.setmore.com/" bookingLabel="Reservar turno (Sucursal Principal)" delay={0} data-id="element-238" />
        <BranchCard name="Sucursal Las Piedras" address="Elias Regules esq. Ruta 67, Las Piedras, Canelones" mapsQuery="Elias Regules y Ruta 67, Las Piedras, Canelones, Uruguay" bookingUrl="https://wa.me/59893472053" bookingLabel="Reservar por WhatsApp" bookingType="whatsapp" delay={0.15} data-id="element-239" />
      </div>
    </section>;
}